import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useTranslation } from 'i18next-vue'
import { useAsyncState } from '@vueuse/core'
import { useRouteParams } from '@vueuse/router'
import { useConfirm } from 'primevue/useconfirm'
import type { Filter } from '@/types/strapi.ts'

/**
 * Base interface for list items that can be managed by this composable
 * All entities must have a documentId for routing and a title for display
 */
interface ListItem {
    documentId?: string
    title?: string
}

/**
 * Configuration object for useEntityList composable
 * @template T - The entity type that extends ListItem
 */
interface UseEntityListConfig<T extends ListItem> {
    /** Optional formatDocumentId passed as prop, falls back to route params if not provided */
    formatDocumentId?: string
    /** Function to load all entities for a given format */
    loadFunction:
        | ((formatId: string) => Promise<T[]>)
        | ((formatId: string, filters?: Filter<T>) => Promise<T[]>)
    /** Function to delete a single entity by its documentId */
    deleteFunction: (id: string) => Promise<void>
    /** Route name for the edit form of this entity type */
    editRouteName?: string
    /** Translation keys for all user-facing messages */
    translations: {
        deleteConfirmMessage: string
        deleteConfirmHeader: string
        deleteConfirmAccept: string
        deleteConfirmReject: string
    }
}

/**
 * Generic composable for managing entity lists (Events, Job Postings, etc.)
 *
 * Provides unified functionality for:
 * - Loading entities with error handling
 * - Edit navigation with correct route parameters
 * - Delete confirmation with toast notifications
 * - Loading states and route parameter management
 *
 * @example
 * ```typescript
 * const { items: events, loading, editItem, confirmDeleteItem } = useEntityList({
 *   formatDocumentId: props.formatDocumentId,
 *   loadFunction: getEventsByFormat,
 *   deleteFunction: deleteEvent,
 *   editRouteName: 'organizer-format-event-form',
 *   translations: { ... }
 * })
 * ```
 */
export function useEntityList<T extends ListItem>({
    formatDocumentId: propFormatDocumentId,
    loadFunction,
    deleteFunction,
    editRouteName,
    translations,
}: UseEntityListConfig<T>) {
    const { t } = useTranslation()
    const router = useRouter()
    const confirm = useConfirm()

    // Automatically resolve formatDocumentId from props or route params
    const routeFormatDocumentId = useRouteParams<string>('formatDocumentId')
    const currentFormatDocumentId = computed(() => {
        return propFormatDocumentId || routeFormatDocumentId.value
    })

    // Load entities with automatic error handling via useAsyncState
    const {
        state: items,
        execute: loadItems,
        isLoading: loading,
        error,
    } = useAsyncState(() => loadFunction(currentFormatDocumentId.value), [])

    /**
     * Navigate to the edit form for the given item
     * Automatically determines the correct parameter name based on route name
     */
    function editItem(item: T) {
        if (item.documentId && editRouteName) {
            // Smart parameter name detection for different entity types
            const paramName = editRouteName.includes('event') ? 'eventId' : 'jobPostingId'

            router.push({
                name: editRouteName,
                params: {
                    formatDocumentId: currentFormatDocumentId.value,
                    [paramName]: item.documentId,
                },
            })
        }
    }

    /**
     * Show confirmation dialog before deleting an item
     * Uses PrimeVue's confirm service with configurable translations
     */
    function confirmDeleteItem(item: T) {
        confirm.require({
            message: t(translations.deleteConfirmMessage, { title: item.title }),
            header: t(translations.deleteConfirmHeader),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: t(translations.deleteConfirmAccept),
            rejectLabel: t(translations.deleteConfirmReject),
            accept: () => handleDeleteItem(item),
        })
    }

    /**
     * Delete an item and handle success/error states
     * Automatically refreshes the list on successful deletion
     */
    async function handleDeleteItem(item: T) {
        if (!item.documentId) {
            return
        }

        try {
            await deleteFunction(item.documentId)
            // Refresh the list to reflect changes
            await loadItems()
        } catch (error) {
            // Error handling is done by HTTP interceptors
            console.error('Delete failed:', error)
        }
    }

    return {
        /** Reactive array of loaded items */
        items,
        /** Loading state for the initial data fetch */
        loading,
        /** Error message for display in ErrorContainer */
        error,
        /** Current formatDocumentId resolved from props or route */
        currentFormatDocumentId,
        /** Function to navigate to edit form */
        editItem,
        /** Function to show delete confirmation dialog */
        confirmDeleteItem,
        /** Function to manually reload the list */
        loadItems,
    }
}
